import React, { useEffect, useState, useRef } from 'react';
import { AI_AGENT_ASSETS, DEFAULT_USER_PROFILE } from '../../config/defaultAssetUrls';
import useCurrentUser from '../../hooks/useCurrentUser';

type ArielleSpeechBubbleProps = {
    text: string;
    className?: string;
    typingSpeed?: number;
    onTypingComplete?: () => void;
    onTypingStart?: () => void;
    timeInMs?: number;
    completed?: boolean;
    workingPhrases?: string[];
};

const ArielleSpeechBubble: React.FC<ArielleSpeechBubbleProps> = ({
    text,
    className = '',
    typingSpeed = 10,
    onTypingComplete,
    onTypingStart,
    timeInMs,
    completed = false,
    workingPhrases = []
}) => {
    const [displayedText, setDisplayedText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
    const startTimeRef = useRef<number | null>(null);

    // Reset typing animation when text changes
    useEffect(() => {
        setDisplayedText('');
        setCurrentIndex(0);
        setIsTyping(true);
        onTypingStart?.();
    }, [text, onTypingStart]);

    // Handle progress bar
    useEffect(() => {
        if (!timeInMs) return;

        if (completed) {
            setProgress(100);
            return;
        }

        if (!startTimeRef.current) {
            startTimeRef.current = Date.now();
        }

        const interval = setInterval(() => {
            const elapsed = Date.now() - startTimeRef.current!;
            const newProgress = Math.min(96, (elapsed / timeInMs) * 96);
            setProgress(newProgress);

            // Update working phrase every 2 seconds
            if (elapsed % (timeInMs / (workingPhrases.length || 1)) < 100) {
                setCurrentPhraseIndex((prev) => (prev + 1) % workingPhrases.length);
            }

            if (elapsed >= timeInMs) {
                clearInterval(interval);
            }
        }, 50);

        return () => clearInterval(interval);
    }, [timeInMs, completed, workingPhrases]);

    useEffect(() => {
        if (!isTyping) return;

        if (currentIndex < text.length) {
            const timer = setTimeout(() => {
                setDisplayedText(prev => prev + text[currentIndex]);
                setCurrentIndex(prev => prev + 1);
            }, typingSpeed);

            return () => clearTimeout(timer);
        } else if (currentIndex === text.length && onTypingComplete) {
            setIsTyping(false);
            onTypingComplete();
        }
    }, [currentIndex, text, typingSpeed, onTypingComplete, isTyping]);

    return (
        <>
            {/* <style>{shimmerKeyframes}</style> */}
            <div className={`w-full flex items-start space-x-4 ${className}`}>
                <div className="flex-shrink-0">
                    <img
                        src={AI_AGENT_ASSETS.ARIELLE_AVATAR}
                        alt="Arielle Avatar"
                        className="w-10 h-10 rounded-full border"
                    />
                </div>
                <div className="relative flex-grow">
                    <div className="px-4 py-2 border shadow-md bg-white shadow-primary-500/40 border-primary-500/30 rounded-2xl">
                        <p className="caption1">{displayedText}</p>
                        {timeInMs && (
                            <div className="mt-2">
                                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden relative">
                                    <div
                                        className="bg-primary h-2 rounded-full transition-all duration-300 relative"
                                        style={{ width: `${progress}%` }}
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                                    </div>
                                    <div className="absolute inset-0 animate-progress" />
                                </div>
                                <div className="flex flex-row justify-between">
                                    <p className="text-sm text-gray-600 mt-1">
                                        {progress === 100 ? 'All done' : workingPhrases[currentPhraseIndex]}
                                    </p>
                                    <div className="flex flex-row gap-2 items-center">
                                        <p className="text-sm text-gray-600 mt-1">
                                            {`${progress.toFixed(0)}%`}
                                        </p>
                                        {!completed && <div className="w-2 h-2 rounded-full animate-spin bg-gradient-to-r from-primary to-white" />}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};


type ArielleResponseBubbleProps = {
    className?: string;
    children: React.ReactNode;
    onConfirm?: () => void;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
};

export const ArielleResponseBubble: React.FC<ArielleResponseBubbleProps> = ({
    className = '',
    children,
    onConfirm,
    onCancel,
    confirmText = 'Continue',
    cancelText = 'Cancel'
}) => {

    const { currentUser } = useCurrentUser();

    return (
        <div className={`flex items-start gap-4 flex-row-reverse ${className}`}>
            <div className="flex-shrink-0">
                <img
                    src={currentUser?.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_EMPLOYER_PROFILE_PICTURE}
                    alt="User Avatar"
                    className="w-10 h-10 rounded-full border ring ring-primary/20"
                />
            </div>

            <div className="w-full flex flex-col gap-2">
                <div className="px-4 py-2 border shadow-md bg-white shadow-primary/50 border-primary/50 rounded-2xl">
                    {children}
                </div>

                <div className="flex flex-row justify-end gap-2">
                    {onCancel && (
                        <button
                            onClick={onCancel}
                            className="px-3 py-1 text-sm border shadow-md bg-white shadow-orange-300/50 border-orange-300/50 rounded-xl transition-colors  hover:bg-orange-300/10"
                        >
                            {cancelText}
                        </button>
                    )}
                    {onConfirm && (
                        <button
                            onClick={onConfirm}
                            className="px-3 py-1 text-sm border shadow-md bg-white shadow-primary/50 border-primary/50 rounded-xl transition-colors hover:bg-primary/10"
                        >
                            {confirmText}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};


export default ArielleSpeechBubble;